import React, { useState, useEffect } from "react";
import qs from "query-string";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";

import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";

import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    background:
      "radial-gradient(circle at top, #ecfdf3 0, #f9fafb 45%, #eef2ff 100%)",
  },
  paper: {
    marginTop: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    background: theme.palette.background.paper,
    padding: theme.spacing(4),
    borderRadius: 18,
    maxWidth: 520,
    width: "100%",
    boxShadow:
      "0 22px 60px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(148, 163, 184, 0.18)",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(3),
      marginTop: theme.spacing(2),
    },
  },
  avatar: {
    margin: theme.spacing(1),
    background: "linear-gradient(135deg, #22c55e, #16a34a)",
    width: 56,
    height: 56,
    boxShadow: "0 12px 28px rgba(22, 163, 74, 0.45)",
  },
  title: {
    marginTop: theme.spacing(1),
    fontWeight: 600,
  },
  subtitle: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(2.5),
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
    textAlign: "center",
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 1.5),
    fontWeight: 600,
    borderRadius: 9999,
    padding: theme.spacing(1.3, 2),
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    boxShadow: "0 12px 32px rgba(22, 163, 74, 0.45)",
  },
  planGrid: {
    marginTop: theme.spacing(1),
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gridGap: theme.spacing(1.5),
  },
  planCard: {
    borderRadius: 16,
    border: "1px solid rgba(148, 163, 184, 0.35)",
    padding: theme.spacing(1.8, 1.6),
    backgroundColor: "#ffffff",
    cursor: "pointer",
    transition:
      "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease",
    "&:hover": {
      borderColor: "#22c55e",
      boxShadow: "0 14px 32px rgba(22, 163, 74, 0.18)",
      transform: "translateY(-1px)",
    },
  },
  planCardSelected: {
    borderColor: "#22c55e",
    boxShadow: "0 16px 40px rgba(22, 163, 74, 0.22)",
    background:
      "linear-gradient(135deg, rgba(240, 253, 244, 0.9), #ffffff)",
  },
  planName: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: "#0f172a",
  },
  planMeta: {
    marginTop: theme.spacing(0.5),
    fontSize: "0.8rem",
    color: "#6b7280",
  },
  planAmount: {
    marginTop: theme.spacing(0.8),
    fontWeight: 600,
    fontSize: "0.95rem",
    color: "#16a34a",
  },
  loginLinkWrapper: {
    marginTop: theme.spacing(1.5),
  },
}));

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Obrigatório"),
  companyName: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Obrigatório"),
  password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email").required("Obrigatório"),
  phone: Yup.string().required("Obrigatório"),
  // Se quiser forçar escolha do plano:
  // planId: Yup.string().required("Selecione um plano"),
});

const SignUp = () => {
  const classes = useStyles();
  const history = useHistory();
  const { getPlanList } = usePlans();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userCreationEnabled, setUserCreationEnabled] = useState(true);

  let companyId = null;
  const params = qs.parse(window.location.search);
  if (params.companyId !== undefined) {
    companyId = params.companyId;
  }

  const initialState = {
    name: "",
    email: "",
    password: "",
    phone: "",
    companyId,
    companyName: "",
    planId: "",
  };

  const [user] = useState(initialState);

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL === "https://localhost:8090"
      ? "https://localhost:8090"
      : process.env.REACT_APP_BACKEND_URL;

  // Verificar se cadastro está habilitado
  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const response = await fetch(`${backendUrl}/settings/userCreation`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user creation status");
        }

        const data = await response.json();
        const isEnabled = data.userCreation === "enabled";
        setUserCreationEnabled(isEnabled);

        if (!isEnabled) {
          toast.info("Cadastro de novos usuários está desabilitado.");
          history.push("/login");
        }
      } catch (err) {
        console.error("Erro ao verificar userCreation:", err);
        setUserCreationEnabled(false);
        toast.error("Erro ao verificar permissão de cadastro.");
        history.push("/login");
      }
    };

    fetchUserCreationStatus();
  }, [backendUrl, history]);

  // Buscar planos
  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      const planList = await getPlanList({ listPublic: "false" });
      setPlans(planList);
      setLoading(false);
    };
    fetchData();
  }, [getPlanList]);

  const handleSignUp = async (values) => {
    try {
      await openApi.post("/auth/signup", values);
      toast.success(i18n.t("signup.toasts.success"));
      history.push("/login");
    } catch (err) {
      toastError(err);
    }
  };

  if (!userCreationEnabled) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Container component="main" maxWidth="sm">
        <CssBaseline />
        <div className={classes.paper}>
          <Avatar className={classes.avatar}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5" className={classes.title}>
            {i18n.t("signup.title")}
          </Typography>
          <Typography className={classes.subtitle}>
            Crie sua conta, selecione o plano ideal e comece a organizar
            seus atendimentos em poucos minutos.
          </Typography>

          <Formik
            initialValues={user}
            enableReinitialize={true}
            validationSchema={UserSchema}
            onSubmit={(values, actions) => {
              setTimeout(() => {
                handleSignUp(values);
                actions.setSubmitting(false);
              }, 400);
            }}
          >
            {({
              touched,
              errors,
              isSubmitting,
              values,
              setFieldValue,
            }) => (
              <Form className={classes.form}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      id="companyName"
                      label={i18n.t("signup.form.company")}
                      error={
                        touched.companyName && Boolean(errors.companyName)
                      }
                      helperText={touched.companyName && errors.companyName}
                      name="companyName"
                      autoComplete="companyName"
                      autoFocus
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      autoComplete="name"
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      fullWidth
                      id="name"
                      label={i18n.t("signup.form.name")}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      id="email"
                      label={i18n.t("signup.form.email")}
                      name="email"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      autoComplete="email"
                      inputProps={{ style: { textTransform: "lowercase" } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      name="password"
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      label={i18n.t("signup.form.password")}
                      type="password"
                      id="password"
                      autoComplete="current-password"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      id="phone"
                      label={i18n.t("signup.form.phone")}
                      name="phone"
                      autoComplete="phone"
                    />
                  </Grid>

                  {/* PLANOS EM CARDS */}
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Selecione um plano
                    </Typography>

                    <div className={classes.planGrid}>
                      {plans.map((plan) => {
                        const selected =
                          String(values.planId) === String(plan.id);

                        return (
                          <Box
                            key={plan.id}
                            className={
                              selected
                                ? `${classes.planCard} ${classes.planCardSelected}`
                                : classes.planCard
                            }
                            onClick={() =>
                              setFieldValue("planId", plan.id)
                            }
                          >
                            <div className={classes.planName}>
                              {plan.name}
                            </div>
                            <div className={classes.planMeta}>
                              Atendentes: {plan.users} • WhatsApp:{" "}
                              {plan.connections} • Filas: {plan.queues}
                            </div>
                            <div className={classes.planAmount}>
                              R$ {plan.amount}
                            </div>
                          </Box>
                        );
                      })}
                    </div>
                  </Grid>
                </Grid>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={isSubmitting || loading}
                >
                  {i18n.t("signup.buttons.submit")}
                </Button>

                <Grid
                  container
                  justifyContent="center"
                  className={classes.loginLinkWrapper}
                >
                  <Grid item>
                    <Link
                      href="#"
                      variant="body2"
                      component={RouterLink}
                      to="/login"
                    >
                      {i18n.t("signup.buttons.login")}
                    </Link>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </div>
        <Box mt={4} />
      </Container>
    </Box>
  );
};

export default SignUp;
